/**
 * RefundsView
 * 
 * Refund Policy page for Plot Planet.
 */

import { PolicyView } from './PolicyView'
import { BRANDING } from '../config/branding'

export function RefundsView() {
  return (
    <PolicyView title="Refund Policy" lastUpdated="January 20, 2026">
      <h2>1. Overview</h2>
      <p>
        At {BRANDING.name}, we want you to be completely satisfied with your purchase. This 
        Refund Policy outlines when and how you can request a refund for our services.
      </p>

      <h2>2. Subscription Refunds</h2>
      
      <h3>2.1 Monthly Subscriptions</h3>
      <p>
        Monthly subscription payments are generally non-refundable. However, we may provide 
        a prorated refund or credit at our discretion if:
      </p>
      <ul>
        <li>You experience significant technical issues that prevent use of the Service</li>
        <li>You cancel within 48 hours of your first payment and have not substantially used the Service</li>
      </ul>

      <h3>2.2 Yearly Subscriptions</h3>
      <p>
        For yearly subscriptions, you may request a prorated refund within 30 days of purchase 
        if you are not satisfied with the Service. After 30 days, refunds are provided at our 
        discretion based on usage and circumstances.
      </p>

      <h2>3. Lifetime Access Refunds</h2>
      <p>
        Lifetime Access purchases may be refunded within 14 days of purchase if:
      </p>
      <ul>
        <li>You have not substantially used the Service (created projects, uploaded content, etc.)</li>
        <li>You contact us with a valid reason for the refund</li>
      </ul>
      <p>
        After 14 days, Lifetime Access purchases are generally non-refundable as this is a 
        one-time purchase for permanent access.
      </p>

      <h2>4. Pay-per-Project Refunds</h2>
      <p>
        Individual project purchases may be refunded within 7 days if:
      </p>
      <ul>
        <li>The project has not been substantially used</li>
        <li>No team members have been added to the project</li>
        <li>No significant content has been created</li>
      </ul>

      <h2>5. Technical Issues</h2>
      <p>
        If you experience persistent technical problems that significantly impact your ability 
        to use {BRANDING.name}, please contact our support team. We will work to resolve the 
        issue, and may offer refunds, credits, or extended service if problems cannot be resolved.
      </p>

      <h2>6. How to Request a Refund</h2>
      <p>To request a refund:</p>
      <ol>
        <li>Email us at <a href="mailto:support@plotplanet.com">support@plotplanet.com</a></li>
        <li>Include your account email address</li>
        <li>Describe the reason for your refund request</li>
        <li>Provide the date and type of purchase</li>
      </ol>
      <p>
        We aim to respond to all refund requests within 2 business days.
      </p>

      <h2>7. Refund Processing</h2>
      <p>
        Approved refunds are processed through Stripe, the same payment processor used for 
        your original purchase. Please allow:
      </p>
      <ul>
        <li>3-5 business days for the refund to be processed</li>
        <li>Additional time for your bank or credit card company to post the refund</li>
      </ul>
      <p>
        Refunds will be issued to the original payment method used for the purchase.
      </p>

      <h2>8. Disputes</h2>
      <p>
        If you believe you are entitled to a refund that was denied, you may contact us to 
        discuss your situation. We review each case individually and strive for fair resolutions.
      </p>
      <p>
        If you initiate a chargeback through your bank or credit card company without first 
        contacting us, we reserve the right to suspend your account and contest the chargeback.
      </p>

      <h2>9. Exceptions</h2>
      <p>
        Refunds will not be provided for:
      </p>
      <ul>
        <li>Accounts terminated due to violation of our Terms of Service</li>
        <li>Services that have been substantially used</li>
        <li>Requests made well outside the refund windows specified above</li>
        <li>Promotional or discounted purchases (may have modified refund terms)</li>
      </ul>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Refund Policy from time to time. Changes will apply to purchases 
        made after the updated policy is posted.
      </p>

      <h2>11. Contact Us</h2>
      <div className="policy-contact">
        <p>
          For refund requests or questions about this policy, please contact us:
        </p>
        <a href="mailto:support@plotplanet.com">support@plotplanet.com</a>
        <a href="mailto:hello@plotplanet.com">hello@plotplanet.com</a>
      </div>
    </PolicyView>
  )
}

export default RefundsView
